
import { Textarea } from "@/components/ui/textarea";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { StartupMention } from "./StartupMention";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface PostInputProps {
  prompt: string;
  setPrompt: (value: string) => void;
  showMentions: boolean;
  mentionUsers: Array<{ id: string; username: string; avatar_url: string | null; }>;
  handleInput: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSelectMention: (username: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  maxLength: number;
}

export const PostInput = ({
  prompt,
  setPrompt,
  showMentions,
  mentionUsers,
  handleInput,
  handleSelectMention,
  textareaRef,
}: PostInputProps) => {
  const [showStartupMention, setShowStartupMention] = useState(false);
  const [startupSearchTerm, setStartupSearchTerm] = useState("");
  const [selectedStartup, setSelectedStartup] = useState<{ id: string; name: string } | null>(null);
  const [isProcessingBusinessPlan, setIsProcessingBusinessPlan] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Detect $ symbol for startup mentions
  useEffect(() => {
    const match = prompt.match(/\$([^$\s]*)$/);
    if (match) {
      setStartupSearchTerm(match[1]);
      setShowStartupMention(true);
    } else {
      setShowStartupMention(false);
    }
  }, [prompt]);

  // Check for special pattern: @kickstartagent mentioned alongside $startup
  useEffect(() => {
    const hasKickstartAgent = prompt.match(/@kickstartagent\b/i);
    const hasSelectedStartup = selectedStartup && prompt.includes(`$${selectedStartup.name}`);
    
    if (hasKickstartAgent && hasSelectedStartup && !isProcessingBusinessPlan) {
      handleBusinessPlanRequest();
    }
  }, [prompt, selectedStartup]);

  const handleBusinessPlanRequest = async () => {
    if (!selectedStartup || isProcessingBusinessPlan) return;
    
    try {
      setIsProcessingBusinessPlan(true);
      
      // Get current user ID
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("Error getting user:", userError);
        throw new Error("Authentication error: " + userError.message);
      }
      
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to use KickstartAgent",
          variant: "destructive",
        });
        setIsProcessingBusinessPlan(false);
        return;
      }
      
      toast({
        title: "Request Received",
        description: "KickstartAgent is analyzing your startup and creating a business plan...",
      });
      
      console.log("Calling generate-business-plan function with:", {
        startupId: selectedStartup.id,
        userId: user.id,
      });
      
      // Call our edge function to generate the business plan
      const response = await supabase.functions.invoke('generate-business-plan', {
        body: {
          startupId: selectedStartup.id,
          userId: user.id,
        }
      });
      
      console.log("Function response:", response);
      
      if (response.error) {
        console.error("Function error:", response.error);
        throw new Error(response.error.message || "Unknown error occurred");
      }
      
      // Clear the input after successful request
      setPrompt("");
      setSelectedStartup(null);
      
      // Refresh feed posts to show the new post
      queryClient.invalidateQueries({ queryKey: ["feed-posts"] });
      
      toast({
        title: "Business Plan Created",
        description: "KickstartAgent has posted a business plan for your startup in the feed",
      });
    } catch (error) {
      console.error("Error generating business plan:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate business plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessingBusinessPlan(false);
    }
  };

  const handleSelectStartup = (startup: { id: string; name: string }) => {
    // Store the selected startup
    setSelectedStartup(startup);
    
    // Replace the $term with the selected startup
    const newText = prompt.replace(/\$([^$\s]*)$/, `$${startup.name} `);
    setPrompt(newText);
    setShowStartupMention(false);
    
    // Focus the textarea and place cursor at the end
    if (textareaRef.current) {
      textareaRef.current.focus();
      const length = newText.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  };

  const renderContent = (text: string) => {
    const parts = [];
    const mentionRegex = /@(\w+)/g;
    const startupRegex = /\$(\w+)/g;
    const hashtagRegex = /#(\w+)/g;
    let lastIndex = 0;
    let match;

    // Process @mentions
    while ((match = mentionRegex.exec(text)) !== null) {
      if (lastIndex < match.index) {
        parts.push(text.slice(lastIndex, match.index));
      }
      
      // Highlight @kickstartagent in a special color
      const isKickstartAgent = match[1].toLowerCase() === 'kickstartagent';
      parts.push(
        <span 
          key={`mention-${match.index}`} 
          className={`inline-block ${isKickstartAgent ? 'text-yellow-400 font-medium' : 'text-[#9b87f5]'}`}
        >
          {match[0]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    // Process remaining text
    let remaining = text.slice(lastIndex);
    lastIndex = 0;
    
    // Process $startups
    const startupParts = [];
    while ((match = startupRegex.exec(remaining)) !== null) {
      if (lastIndex < match.index) {
        startupParts.push(remaining.slice(lastIndex, match.index));
      }
      startupParts.push(
        <span key={`startup-${match.index}`} className="text-flux-cyan inline-block">
          {match[0]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }
    
    // Process remaining text for hashtags
    remaining = remaining.slice(lastIndex);
    lastIndex = 0;
    
    // Process #hashtags
    const hashtagParts = [];
    while ((match = hashtagRegex.exec(remaining)) !== null) {
      if (lastIndex < match.index) {
        hashtagParts.push(remaining.slice(lastIndex, match.index));
      }
      hashtagParts.push(
        <span key={`hashtag-${match.index}`} className="text-[#9b87f5] inline-block">
          {match[0]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < remaining.length) {
      hashtagParts.push(remaining.slice(lastIndex));
    }

    // Combine all parts
    parts.push(...startupParts);
    parts.push(...hashtagParts);

    return <div className="whitespace-pre-wrap text-white">{parts}</div>;
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={prompt}
        onChange={handleInput}
        placeholder="What's on your mind? Try mentioning @kickstartagent and $startup to generate a business plan!"
        className="bg-transparent border-flux-cyan/20 focus-visible:ring-flux-cyan/30 resize-none text-transparent bg-clip-text min-h-[80px] text-sm"
      />
      <div className="absolute inset-0 pointer-events-none">
        <div className="p-3">
          {renderContent(prompt)}
        </div>
      </div>
      
      {showStartupMention && (
        <StartupMention 
          searchTerm={startupSearchTerm} 
          onSelect={handleSelectStartup} 
          isVisible={showStartupMention} 
        />
      )}
      
      {showMentions && (
        <div className="absolute bottom-full left-0 w-full mb-1 rounded-md border bg-black/90 shadow-lg">
          <Command>
            <CommandList>
              <CommandEmpty>No users found</CommandEmpty>
              <CommandGroup>
                {mentionUsers.map((user) => (
                  <CommandItem
                    key={user.id}
                    onSelect={() => handleSelectMention(user.username)}
                    className="flex items-center gap-2 cursor-pointer py-2"
                  >
                    <img
                      src={user.avatar_url || "/placeholder.svg"}
                      alt={user.username}
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="text-white">@{user.username}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
};
